import * as React from 'react';
import { Box, Dialog } from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import { useEffect, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { useKeyPress } from '@react-typed-hooks/use-key-press';
import { AddStaffInfoRequest } from 'types/add-staff-user/add-staff-info';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import { RowDataType, TableMetaData } from 'types/common/common';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddStaff from './add-staff';
import ChangeStaffPassword from 'pages/staff-management/change-password';
import { useAddStaffMutation } from 'store/slices/staff/add-staff';
import { useStaffListQuery } from 'store/slices/staff/staff-list';
import { useLazyDeleteStaffQuery } from 'store/slices/staff/delete-staff';
import FlexibleTable from 'component/flexible-table';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import { DeleteStaffRequest } from 'types/add-staff-user/delete-staff';
import { StyledTableRow } from 'component/tabel-pagination-style';
import Filter from 'component/filter';

export const initialMainFilterData: AddStaffInfoRequest = {};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'email',
        position: 1,
        title: 'Email',
        isSortable: false,
    },
    {
        key: 'firstName',
        position: 2,
        title: 'First Name',
        isSortable: false,
    },
    {
        key: 'lastName',
        position: 3,
        title: 'Last Name',
        isSortable: false,
    },
    {
        key: 'action',
        position: 4,
        title: 'Action',
        isSortable: false,
    },
];

const StaffManagement: React.FC = () => {
    const [openDialogAddStaff, setOpenDialogAddStaff] = React.useState(false);
    const [staffId, setStaffId] = React.useState<string>('');
    const [openDialogChangePassword, setDialogChangePassword] =
        React.useState(false);
    const [staffInfo, setStaffInfo] = useState<AddStaffInfoRequest>(
        initialMainFilterData,
    );
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [sort, setSort] = useState<string>('');
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const dispatch = useAppDispatch();

    const { data } = useStaffListQuery();
    const [staffInfoApi, { error, isError, isSuccess }] = useAddStaffMutation();
    const [deleteStaff] = useLazyDeleteStaffQuery();

    const isPressed = useKeyPress({ targetKey: 'Enter' });

    const validateFilterData = (): boolean => {
        let isValid = true;
        let errorText = '';

        if (staffInfo.email == null || undefined) {
            isValid = false;
            errorText = 'Email fields are empty';
        }

        if (staffInfo.lastName == '' || staffInfo.lastName == 'Invalid Date') {
            isValid = false;
            errorText = 'LastName fields are empty';
        }
        if (
            staffInfo.firstName == '' ||
            staffInfo.firstName == 'Invalid Date'
        ) {
            isValid = false;
            errorText = 'FirstName fields are empty';
        }
        if (staffInfo.password == '' || staffInfo.password == 'Invalid Date') {
            isValid = false;
            errorText = 'Password fields are empty';
        }

        if (!isValid) {
            dispatch(
                openSnackbar({
                    message: errorText,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }

        return isValid;
    };

    const createNewStaff = () => {
        setOpenDialogAddStaff(true);
    };

    const closeAddStaff = () => {
        setOpenDialogAddStaff(false);
    };

    const closeDialogChangePassword = () => {
        setDialogChangePassword(false);
    };

    const handelClickChangePassword = async (staffId: string) => {
        await setDialogChangePassword(true);
        await setStaffId(staffId);
    };

    const handelClickDeleteStaff = async (
        deleteStaffRequest: DeleteStaffRequest,
    ) => {
        if (deleteStaffRequest) {
            await deleteStaff(deleteStaffRequest);
        }
    };

    const handleAddStaffClick = () => {
        if (validateFilterData()) {
            staffInfoApi(staffInfo);
        }
    };

    useEffect(() => {
        if (isPressed) {
            handleAddStaffClick();
        }
    }, [isPressed]);

    useEffect(() => {
        if (isSuccess) {
            setOpenDialogAddStaff(false);
        }
    }, [isSuccess]);

    useEffect(() => {
        const tableData: RowDataType[] = data
            ? data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      action: (
                          <Box display={'flex'} justifyContent={'space-around'}>
                              <Edit
                                  cursor="pointer"
                                  onClick={() =>
                                      handelClickChangePassword(fullInfo.id)
                                  }
                              />
                              <Delete
                                  cursor="pointer"
                                  onClick={() => {
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Delete',
                                              handleConfirm:
                                                  handelClickDeleteStaff,
                                              parameters: { id: fullInfo.id },
                                          }),
                                      );
                                  }}
                              />
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Filter display={true} />
            <Box
                mt={pxToRem(17)}
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewStaff}
                    buttonName={'Creat New Staff'}
                />
            </Box>
            <Dialog open={openDialogAddStaff} onClose={closeAddStaff}>
                <AddStaff
                    closeModal={closeAddStaff}
                    staffInfo={staffInfo}
                    setStaffInfo={setStaffInfo}
                    handleAddStaffClick={handleAddStaffClick}
                />
            </Dialog>
            <Dialog
                open={openDialogChangePassword}
                onClose={closeDialogChangePassword}
            >
                <ChangeStaffPassword
                    closeModal={closeDialogChangePassword}
                    staffId={staffId}
                />
            </Dialog>
        </>
    );
};

export default StaffManagement;
